// Pure status → { tone, label } mappers behind StatusPill — kept out of
// the .tsx component so node-pool unit tests (tests/web/*) can import
// them without a JSX compile. StatusPill.tsx re-exports everything here,
// so call sites keep importing from "../StatusPill" unchanged.

// The dot tones. Each maps to a status-dot token in styles.css.
export type StatusTone = "active" | "review" | "stalled" | "neutral" | "info";

// Sentence-case a raw status slug for an unmapped value ("foo_bar" → "Foo bar").
export function sentenceCase(slug: string): string {
  const spaced = slug.replace(/_/g, " ").trim();
  if (!spaced) return slug;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

// Service-record status → tone + sentence-case label. Active is the only
// "good" green; replaced is amber (a switched carrier needs a glance);
// everything terminal/dormant is neutral gray. Unknown statuses fall back
// to a neutral dot with a sentence-cased label so nothing renders raw.
const SERVICE_RECORD_STATUS: Record<
  string,
  { tone: StatusTone; label: string }
> = {
  active: { tone: "active", label: "Active" },
  past: { tone: "neutral", label: "Past" },
  inactive: { tone: "neutral", label: "Inactive" },
  replaced: { tone: "review", label: "Replaced" },
  archived: { tone: "neutral", label: "Archived" },
};

export function serviceRecordStatus(status: string): {
  tone: StatusTone;
  label: string;
} {
  return (
    SERVICE_RECORD_STATUS[status] ?? {
      tone: "neutral",
      label: sentenceCase(status),
    }
  );
}

// Loan-disposition annotation (Grant's ruling, 2026-07-11): a past loan
// that FUNDED ("Past · Closed") reads differently from one that FELL
// THROUGH ("Past · Cancelled"). The disposition is a derived Category-1
// field off mortgage_loans dates (shared/db/loan-disposition.ts) — the
// SR status enum itself has no 'cancelled' value, on purpose.
const LOAN_DISPOSITION_SUFFIX: Record<string, string> = {
  closed: "Closed",
  cancelled: "Cancelled",
};

// The year out of a preformatted date label ("Mar 2, 2025" → "2025").
// The label is produced by formatDate() upstream; a 4-digit run is the
// year in every format it emits. Returns null when there isn't one, so
// an unexpected shape degrades to the old hover-only behavior rather
// than printing garbage into the pill.
function yearFromDateLabel(dateLabel: string): string | null {
  return /\b(\d{4})\b/.exec(dateLabel)?.[1] ?? null;
}

/**
 * serviceRecordStatus + the loan-disposition suffix. Same tone as the
 * base status (no new design language — the distinction lives in the
 * label). Only annotates PAST loans: an active loan's disposition is
 * workflow state, not something the status pill should double-report.
 * `dispositionDateLabel` (a preformatted date, e.g. "Mar 2, 2025")
 * supplies the visible year AND the fuller hover title.
 *
 * WHY THE YEAR IS VISIBLE (Baker, 2026-08-11). The date used to live in
 * the hover title alone. Hunting for a mortgage he had just closed, he
 * clicked down a household's list of identical "Past · Closed" rows and
 * gave up: "it would be helpful to know the year… that way it would be
 * easier to go, yes." Hover is not discoverable mid-hunt and does not
 * exist on touch. A year is four characters and makes the rows tellable
 * apart at a glance; the full date stays on hover for when it matters.
 */
export function serviceRecordStatusWithDisposition(
  status: string,
  loanDisposition?: string | null,
  dispositionDateLabel?: string | null,
): { tone: StatusTone; label: string; title?: string } {
  const base = serviceRecordStatus(status);
  const suffix =
    status === "past" && loanDisposition
      ? LOAN_DISPOSITION_SUFFIX[loanDisposition]
      : undefined;
  if (!suffix) return base;
  const year = dispositionDateLabel
    ? yearFromDateLabel(dispositionDateLabel)
    : null;
  return {
    tone: base.tone,
    label: `${base.label} · ${suffix}${year ? ` ${year}` : ""}`,
    ...(dispositionDateLabel
      ? { title: `${suffix} ${dispositionDateLabel}` }
      : {}),
  };
}

// service_records.status is a per-company resting-state field (a Realty
// Listing rests at 'inactive' until under contract; an Insurance Policy
// rests at 'active' the moment it's bound) — it is NOT a workflow signal.
// active_workflow_bucket ("opp" | "proc" | "care" | null) is the actual
// "is there open work on this right now" answer, computed independently
// off open Opportunities / open Transactions / active Rhythms. Rendering
// `status` alone let a Realty deal read "Inactive" while its Processing
// transaction was actively moving — the two axes disagreed. Whenever
// there's an active workflow bucket, that always wins: the record is
// unambiguously in flight, whatever its resting-state status says.
export function serviceRecordStatusWithWorkflow(
  status: string,
  activeWorkflowBucket: "opp" | "proc" | "care" | null,
  loanDisposition?: string | null,
  dispositionDateLabel?: string | null,
): { tone: StatusTone; label: string; title?: string } {
  if (activeWorkflowBucket && status !== "active") {
    return { tone: "active", label: "Active" };
  }
  return serviceRecordStatusWithDisposition(
    status,
    loanDisposition,
    dispositionDateLabel,
  );
}

// E-signature envelope status (send half, Phase 1). BoldSign's own
// status word is stored verbatim (esign_envelopes.status), plus the
// OS's own "Cancelled" when an advisor calls one back — mapped here
// rather than adding a second parallel status vocabulary.
const ESIGN_ENVELOPE_STATUS: Record<string, { tone: StatusTone; label: string }> = {
  completed: { tone: "active", label: "Signed" },
  signed: { tone: "active", label: "Signed" },
  sent: { tone: "info", label: "Waiting on signature" },
  inprogress: { tone: "info", label: "Waiting on signature" },
  "in progress": { tone: "info", label: "Waiting on signature" },
  declined: { tone: "stalled", label: "Declined" },
  expired: { tone: "stalled", label: "Expired" },
  sendfailed: { tone: "stalled", label: "Didn't send" },
  cancelled: { tone: "neutral", label: "Cancelled" },
  revoked: { tone: "neutral", label: "Revoked" },
};

export function esignEnvelopeStatus(status: string): { tone: StatusTone; label: string } {
  return (
    ESIGN_ENVELOPE_STATUS[status.toLowerCase()] ?? { tone: "neutral", label: sentenceCase(status) }
  );
}

// One SIGNER's state, as a record panel says it (2026-09-19). The
// envelope pill answers "where is this document?"; this answers "where
// is this person?", which is the question an advisor looking at a
// half-signed document is actually asking — Nate, on a partly-signed
// disclosure: "i also don't see who".
//
// Null is its own answer and deliberately not folded into "pending": we
// have heard NOTHING about this person, which on an old envelope can
// mean we were not recording yet, and on a new one means the delivery
// report has not come back. "Sent, no news yet" says that honestly; a
// cheerful "Sent" would claim more than we know.
const ESIGN_SIGNER_STATUS: Record<string, { tone: StatusTone; label: string }> = {
  signed: { tone: "active", label: "Signed" },
  declined: { tone: "stalled", label: "Declined" },
  viewed: { tone: "info", label: "Opened, not signed" },
  delivered: { tone: "info", label: "Delivered" },
  pending: { tone: "neutral", label: "Sent" },
};

export function esignSignerStatus(
  status: string | null | undefined,
): { tone: StatusTone; label: string } {
  if (!status) return { tone: "neutral", label: "Sent, no news yet" };
  return (
    ESIGN_SIGNER_STATUS[status.toLowerCase()] ?? { tone: "neutral", label: sentenceCase(status) }
  );
}
